import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./BookDemoModal.css";

// Public Formspree endpoint, hardcoded like the one in Contact.jsx. Amplify has no env vars set for this app.
const FORM_URL = "https://formspree.io/f/xppporvz";
const SOURCE = "genieshop-landing";
const FALLBACK_EMAIL = "info@consumergenie.net";

// Why: warn only. Blocking would reject genuine solo founders who use a personal address.
const FREE_EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
];

const ROLE_OPTIONS = ["Founder / Owner", "Ecommerce lead", "Developer", "Agency", "Other"];
const PLATFORM_OPTIONS = ["Shopify", "WooCommerce", "BigCommerce", "Magento", "Custom build", "Other"];

const EMPTY_FORM = {
  name: "",
  email: "",
  company: "",
  storeUrl: "",
  role: "",
  platform: "",
  phone: "",
  message: "",
  // Honeypot. Real users never see or fill it, so any value means a bot.
  website: "",
};

const isFreeEmail = (email) => {
  const domain = email.trim().toLowerCase().split("@")[1];
  return Boolean(domain) && FREE_EMAIL_DOMAINS.includes(domain);
};

function BookDemoModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  // Reset status on reopen so a past thank-you screen does not linger
  useEffect(() => {
    if (isOpen) {
      setSubmitStatus("idle");
      setErrorMessage("");
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot filled: pretend success so the bot learns nothing, send nothing
    if (formData.website) {
      setSubmitStatus("success");
      setFormData(EMPTY_FORM);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");
    setErrorMessage("");

    // Why: build the payload explicitly so the honeypot never reaches the inbox
    const { website, ...fields } = formData;

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          ...fields,
          source: SOURCE,
          _subject: `[Book a Demo] ${formData.company || "New lead"} via GenieShop`,
        }),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData(EMPTY_FORM);
      } else {
        const data = await response.json().catch(() => ({}));
        setSubmitStatus("error");
        setErrorMessage(
          data?.error || `Something went wrong. Please try again or email ${FALLBACK_EMAIL}.`
        );
      }
    } catch {
      setSubmitStatus("error");
      setErrorMessage(`Network error. Please check your connection or email ${FALLBACK_EMAIL}.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Why: closed state renders nothing
  if (!isOpen) return null;

  // Why portal: the Navbar has overflow: hidden and backdrop-filter, which would clip or trap a fixed modal
  return createPortal(
    <div
      className="bd-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="book-demo-title"
      onClick={onClose}
    >
      <div className="bd-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="bd-close" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Why: after success the form is replaced, so a visitor cannot submit the same request twice */}
        {submitStatus === "success" ? (
          <div className="bd-thanks" role="status">
            <div className="bd-thanks-icon" aria-hidden="true">✓</div>
            <h2 id="book-demo-title" className="bd-title">Request received</h2>
            <p className="bd-subtitle">Thank you. We will be in touch within 1 business day.</p>
            <button type="button" className="bd-submit bd-thanks-close" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="bd-header">
              <h2 id="book-demo-title" className="bd-title">Book a Demo</h2>
              <p className="bd-subtitle">
                Tell us about your store. We will reach out within 1 business day.
              </p>
            </div>

            {submitStatus === "error" && (
              <div className="bd-alert bd-alert-error">✗ {errorMessage}</div>
            )}

            <form className="bd-form" onSubmit={handleSubmit}>
              <div className="bd-field">
                <label htmlFor="book-demo-name">Full name</label>
                <input
                  id="book-demo-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  autoComplete="name"
                  placeholder="Your name"
                />
              </div>

              <div className="bd-field">
                <label htmlFor="book-demo-email">Work email</label>
                <input
                  id="book-demo-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                  placeholder="you@yourstore.com"
                />
                {isFreeEmail(formData.email) && (
                  <p className="bd-hint">
                    A work email helps us prepare for your demo. You can still submit with this one.
                  </p>
                )}
              </div>

              <div className="bd-row">
                <div className="bd-field">
                  <label htmlFor="book-demo-company">Company / store name</label>
                  <input
                    id="book-demo-company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    autoComplete="organization"
                    placeholder="Your company"
                  />
                </div>

                <div className="bd-field">
                  <label htmlFor="book-demo-store-url">Store URL</label>
                  <input
                    id="book-demo-store-url"
                    name="storeUrl"
                    type="url"
                    value={formData.storeUrl}
                    onChange={handleChange}
                    required
                    placeholder="https://yourstore.com"
                  />
                </div>
              </div>

              <div className="bd-row">
                <div className="bd-field">
                  <label htmlFor="book-demo-role">Role (optional)</label>
                  <select id="book-demo-role" name="role" value={formData.role} onChange={handleChange}>
                    <option value="">Select</option>
                    {ROLE_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="bd-field">
                  <label htmlFor="book-demo-platform">Platform (optional)</label>
                  <select id="book-demo-platform" name="platform" value={formData.platform} onChange={handleChange}>
                    <option value="">Select</option>
                    {PLATFORM_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="bd-field">
                <label htmlFor="book-demo-phone">Phone (optional)</label>
                <input
                  id="book-demo-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  autoComplete="tel"
                  placeholder="Your phone number"
                />
              </div>

              <div className="bd-field">
                <label htmlFor="book-demo-message">What would you like to see? (optional)</label>
                <textarea
                  id="book-demo-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Anything we should prepare for your demo"
                />
              </div>

              {/* Honeypot. Hidden from people and screen readers, bots tend to fill it. */}
              <div className="bd-honeypot" aria-hidden="true">
                <label htmlFor="book-demo-website">Website</label>
                <input
                  id="book-demo-website"
                  name="website"
                  type="text"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <p className="bd-consent">Your details are used only to arrange your demo.</p>

              <div className="bd-actions">
                <button type="submit" className="bd-submit" disabled={isSubmitting}>
                  {isSubmitting ? "Sending…" : "Book a Demo"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

export default BookDemoModal;
